import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import {
  fetchSodRules,
  fetchSodRulesAuditLog,
  importSodRules,
  exportSodRules,
} from '@/lib/sodRulesApi';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Upload,
  Download,
  FileSpreadsheet,
  X,
  Loader2,
  ShieldAlert,
  History,
} from 'lucide-react';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const RISK_COLORS = {
  critical: 'border-red-400 text-red-600',
  high: 'border-orange-400 text-orange-600',
  medium: 'border-blue-400 text-blue-600',
  low: 'border-gray-400 text-gray-600',
};

function riskColor(risk) {
  return RISK_COLORS[String(risk || '').toLowerCase()] || 'border-gray-400 text-gray-600';
}

function FunctionCell({ fn }) {
  return (
    <div>
      <div className="font-medium text-sm">{fn?.name}</div>
      <div className="text-xs text-muted-foreground font-mono">
        {(fn?.tcodes || []).join(', ')}
      </div>
    </div>
  );
}

export default function SodRules() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  // SoD BFF lives under dashboardapi — dashboard.read gates the page and actions for now.
  const canUseSod = hasPermission('dashboard', 'read');
  const [rules, setRules] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!canUseSod) {
      setRules([]);
      setAuditLog([]);
      setLoading(false);
      return;
    }
    loadData();
  }, [canUseSod]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rulesData, auditData] = await Promise.all([
        fetchSodRules(),
        fetchSodRulesAuditLog(),
      ]);
      setRules(rulesData);
      setAuditLog(auditData);
    } catch (error) {
      toast.error('Failed to load SOD rules');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (!canUseSod) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setConfirmOpen(true);
  };

  const clearFile = () => {
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleConfirmImport = async () => {
    if (!canUseSod || !pendingFile) return;
    setImporting(true);
    try {
      const contentBase64 = await fileToBase64(pendingFile);
      const result = await importSodRules({
        contentBase64,
        filename: pendingFile.name,
        actor: user?.email || 'unknown',
      });
      toast.success(
        `Ruleset replaced: ${result.rows_before} → ${result.rows_after} rows (${result.unique_rule_ids_after} rules)`
      );
      setConfirmOpen(false);
      clearFile();
      await loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message;
      toast.error(`Import failed: ${detail}`);
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    if (!canUseSod) return;
    try {
      await exportSodRules('xlsx', user?.email || 'unknown');
      const auditData = await fetchSodRulesAuditLog();
      setAuditLog(auditData);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const filteredRules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((r) =>
      [
        r.id,
        r.risk,
        r.description,
        r.function_a?.name,
        ...(r.function_a?.tcodes || []),
        r.function_b?.name,
        ...(r.function_b?.tcodes || []),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [rules, search]);

  if (!canUseSod) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            You do not have permission to view SOD Rules.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6" />
            SOD Rules
          </h1>
          <p className="text-muted-foreground">
            Segregation of Duties conflict ruleset — {rules.length} rule
            {rules.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex gap-2">
          <span title={permissionDeniedTitle(canUseSod, 'export SOD rules')}>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={!canUseSod}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
          </span>
          {pendingFile ? (
            <div className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
              <FileSpreadsheet className="h-4 w-4 shrink-0 text-green-600" />
              <span className="max-w-[160px] truncate">{pendingFile.name}</span>
              <button
                type="button"
                onClick={clearFile}
                className="text-green-600 hover:text-green-800"
                aria-label="Remove file"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <span title={permissionDeniedTitle(canUseSod, 'upload SOD rules')}>
              <label
                className={`flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-2 text-sm text-muted-foreground transition-colors ${
                  canUseSod
                    ? 'cursor-pointer hover:border-primary/50 hover:bg-muted/50'
                    : 'cursor-not-allowed opacity-60'
                }`}
              >
                <Upload className="h-4 w-4 shrink-0" />
                <span>Upload .csv / .xlsx</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx"
                  className="sr-only"
                  disabled={!canUseSod}
                  onChange={handleFileChange}
                />
              </label>
            </span>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rules</CardTitle>
          <CardDescription>
            Read-only — rules can only be changed by uploading a replacement file
          </CardDescription>
          <Input
            placeholder="Search by rule ID, risk, description, function name, tcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md mt-2"
          />
        </CardHeader>
        <CardContent>
          <div className="max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>Rule ID</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Function A</TableHead>
                  <TableHead>Function B</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No rules found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRules.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.id}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs border ${riskColor(r.risk)}`}>
                          {r.risk}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{r.description}</TableCell>
                      <TableCell>
                        <FunctionCell fn={r.function_a} />
                      </TableCell>
                      <TableCell>
                        <FunctionCell fn={r.function_b} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Audit Trail
          </CardTitle>
          <CardDescription>Every ruleset replace and export, who did it, and when</CardDescription>
        </CardHeader>
        <CardContent>
          {auditLog.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No audit history yet</div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {auditLog.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-3 border rounded-lg text-sm hover:bg-accent"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="font-mono text-xs">
                        {log.action.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {log.details?.filename && (
                      <div className="text-xs text-muted-foreground">{log.details.filename}</div>
                    )}
                    {log.action === 'bulk_replace' && (
                      <div className="text-xs text-muted-foreground">
                        {log.details.rows_before} → {log.details.rows_after} rows
                        {' • '}
                        {log.details.unique_rule_ids_after} rules
                      </div>
                    )}
                    {log.action === 'export' && (
                      <div className="text-xs text-muted-foreground">
                        {log.details.row_count} rows • {log.details.format}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(log.timestamp).toLocaleString()} • by {log.actor}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => !importing && setConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace all SOD rules?</AlertDialogTitle>
            <AlertDialogDescription>
              Uploading <strong>{pendingFile?.name}</strong> will permanently delete all{' '}
              {rules.length} existing rules and replace them with the contents of this file. This
              action is recorded in the audit trail but cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={importing} onClick={clearFile}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmImport();
              }}
              disabled={importing}
            >
              {importing ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Replacing...
                </span>
              ) : (
                'Replace rules'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
